import * as React from 'react';

/**
 * Filter state for a list page: definitions describe the controls (a single
 * select, a multi-select, or an on/off toggle); values hold what is chosen;
 * chips are derived from both so the toolbar can show "Level: A2 ✕".
 * Pure helpers are exported for tests; the hook wires them to React state.
 */

/** `count` (rows matching this value) is shown muted; a count of 0 hides the option unless selected. */
export type FilterOption = { value: string; label: string; count?: number };

/**
 * `display` forces the presentation of an option section; left out, ≤ 12
 * options render as a chip grid and more as a collapsed searchable list.
 */
export type FilterDisplay = 'chips' | 'list';

export type FilterDef =
  | { key: string; label: string; type: 'select'; options: FilterOption[]; allLabel?: string; display?: FilterDisplay }
  | { key: string; label: string; type: 'multi'; options: FilterOption[]; display?: FilterDisplay }
  | { key: string; label: string; type: 'toggle'; onLabel?: string; display?: undefined };

export type FilterValue = string | string[] | boolean | null | undefined;
export type FilterValues = Record<string, FilterValue>;

export type FilterChip = { key: string; label: string; onRemove: () => void };

/** Sentinel for "no choice" on a select (Radix Select rejects empty strings). */
export const FILTER_ALL = '__all__';

export function isFilterActive(def: FilterDef, value: FilterValue): boolean {
  switch (def.type) {
    case 'select':
      return typeof value === 'string' && value !== '' && value !== FILTER_ALL;
    case 'multi':
      return Array.isArray(value) && value.length > 0;
    case 'toggle':
      return value === true;
  }
}

export function countActiveFilters(defs: FilterDef[], values: FilterValues): number {
  return defs.reduce((n, def) => n + (isFilterActive(def, values[def.key]) ? 1 : 0), 0);
}

export function emptyValueFor(def: FilterDef): FilterValue {
  switch (def.type) {
    case 'select':
      return FILTER_ALL;
    case 'multi':
      return [];
    case 'toggle':
      return false;
  }
}

/**
 * Chips for the active filters. `set` is called with the value that removes
 * the chip (the select back to "all", the option taken out of the multi,
 * the toggle off).
 */
export function deriveFilterChips(
  defs: FilterDef[],
  values: FilterValues,
  set: (key: string, value: FilterValue) => void,
): FilterChip[] {
  const chips: FilterChip[] = [];
  for (const def of defs) {
    const value = values[def.key];
    if (!isFilterActive(def, value)) continue;
    if (def.type === 'select') {
      const option = def.options.find((o) => o.value === value);
      chips.push({
        key: def.key,
        label: `${def.label}: ${option?.label ?? String(value)}`,
        onRemove: () => set(def.key, FILTER_ALL),
      });
    } else if (def.type === 'multi') {
      for (const chosen of value as string[]) {
        const option = def.options.find((o) => o.value === chosen);
        chips.push({
          key: `${def.key}:${chosen}`,
          label: `${def.label}: ${option?.label ?? chosen}`,
          onRemove: () => set(def.key, (value as string[]).filter((v) => v !== chosen)),
        });
      }
    } else {
      chips.push({ key: def.key, label: def.onLabel ?? def.label, onRemove: () => set(def.key, false) });
    }
  }
  return chips;
}

export function useListFilters(defs: FilterDef[], initial: FilterValues = {}) {
  const [values, setValues] = React.useState<FilterValues>(() => {
    const base: FilterValues = {};
    for (const def of defs) base[def.key] = initial[def.key] ?? emptyValueFor(def);
    return base;
  });

  const setValue = React.useCallback((key: string, value: FilterValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clear = React.useCallback(() => {
    setValues(() => {
      const next: FilterValues = {};
      for (const def of defs) next[def.key] = emptyValueFor(def);
      return next;
    });
  }, [defs]);

  const chips = React.useMemo(() => deriveFilterChips(defs, values, setValue), [defs, values, setValue]);
  const activeCount = React.useMemo(() => countActiveFilters(defs, values), [defs, values]);

  const get = React.useCallback(<T extends FilterValue>(key: string) => values[key] as T, [values]);

  return { values, setValue, clear, chips, activeCount, get };
}
